import { useCallback, useMemo, useState } from 'react';

export interface Selection<Row> {
  /** The keys of the ticked rows. */
  selected: ReadonlySet<string>;
  /** The ticked rows themselves, as they were when ticked, in the order ticked. */
  rows: Row[];
  count: number;
  onToggle: (row: Row, selected: boolean) => void;
  onTogglePage: (rows: Row[], selected: boolean) => void;
  clear: () => void;
}

/**
 * The rows ticked on a case table, for the actions on a selection. Section 4 of
 * the request of 24 September 2026.
 *
 * Kept by key with the row itself, so a selection carries on across pages: a
 * reader can tick five on page one and three on page two and act on all eight.
 * The rows are what they were when ticked, which is all an action needs: the job
 * order number, the kit id, the bench they are at.
 *
 * `rowKey` must keep its identity between renders, a function declared outside
 * the component, or every box would be a new callback on every render.
 */
export function useSelection<Row>(rowKey: (row: Row) => string): Selection<Row> {
  const [picked, setPicked] = useState<ReadonlyMap<string, Row>>(() => new Map());

  const onToggle = useCallback(
    (row: Row, on: boolean) =>
      setPicked((previous) => {
        const next = new Map(previous);
        if (on) next.set(rowKey(row), row);
        else next.delete(rowKey(row));
        return next;
      }),
    [rowKey],
  );

  const onTogglePage = useCallback(
    (rows: Row[], on: boolean) =>
      setPicked((previous) => {
        const next = new Map(previous);
        for (const row of rows) {
          if (on) next.set(rowKey(row), row);
          else next.delete(rowKey(row));
        }
        return next;
      }),
    [rowKey],
  );

  const clear = useCallback(() => setPicked(new Map()), []);

  return useMemo(
    () => ({
      selected: new Set(picked.keys()),
      rows: [...picked.values()],
      count: picked.size,
      onToggle,
      onTogglePage,
      clear,
    }),
    [picked, onToggle, onTogglePage, clear],
  );
}
